import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Copy, ExternalLink } from "lucide-react";

const EmailComposeContext = createContext(null);

export function useEmailCompose() {
  const ctx = useContext(EmailComposeContext);
  if (!ctx) throw new Error("useEmailCompose must be used inside <EmailComposeProvider>");
  return ctx;
}

// Providers with a public compose deep-link (recipient prefilled): just open.
// Providers without one (Libero/Aruba/iCloud): auto-copy the address, then
// open the webmail — the user pastes it in the "To" field. No manual copy.
const PROVIDERS = [
  {
    id: "gmail",
    label: "Gmail",
    testid: "email-compose-gmail",
    url: (to) => `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}`,
    copyFirst: false,
  },
  {
    id: "outlook",
    label: "Outlook · Hotmail · Live",
    testid: "email-compose-outlook",
    url: (to) => `https://outlook.live.com/mail/deeplink/compose?to=${encodeURIComponent(to)}`,
    copyFirst: false,
  },
  {
    id: "yahoo",
    label: "Yahoo Mail",
    testid: "email-compose-yahoo",
    url: (to) => `https://compose.mail.yahoo.com/?to=${encodeURIComponent(to)}`,
    copyFirst: false,
  },
  {
    id: "libero",
    label: "Libero",
    testid: "email-compose-libero",
    url: () => "https://login.libero.it/",
    copyFirst: true,
    note: "Indirizzo copiato · incollalo nel destinatario",
  },
  {
    id: "aruba",
    label: "Aruba (Webmail)",
    testid: "email-compose-aruba",
    url: () => "https://webmail.aruba.it/",
    copyFirst: true,
    note: "Indirizzo copiato · incollalo nel destinatario",
  },
  {
    id: "icloud",
    label: "iCloud Mail",
    testid: "email-compose-icloud",
    url: () => "https://www.icloud.com/mail",
    copyFirst: true,
    note: "Indirizzo copiato · incollalo nel destinatario",
  },
];


async function copyToClipboard(value) {
  const fallback = () => {
    try {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, value.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  };
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch { /* fallback */ }
  return fallback();
}


export function EmailComposeProvider({ children }) {
  const [email, setEmail] = useState(null);
  // Tracks whether we have pushed a history entry for the current open dialog,
  // so the browser back button/gesture closes the dialog instead of navigating.
  const pushedHistory = useRef(false);
  const closingViaPopstate = useRef(false);

  const open = useCallback((address) => setEmail(address), []);
  const close = useCallback(() => setEmail(null), []);
  // Defer close by one tick so any pending navigation completes first.
  const closeLater = useCallback(() => { setTimeout(() => setEmail(null), 0); }, []);

  // Push a history state when the dialog opens, and pop it when it closes.
  useEffect(() => {
    if (email) {
      // Opening: push a marker state so back button closes the dialog.
      if (!pushedHistory.current) {
        try {
          window.history.pushState({ __emailDialog: true }, "", window.location.href);
          pushedHistory.current = true;
        } catch { /* pushState unavailable — no-op */ }
      }
      const onPop = () => {
        // The user pressed back / swiped back — close the dialog without
        // consuming another history entry (browser already did).
        closingViaPopstate.current = true;
        pushedHistory.current = false;
        setEmail(null);
      };
      window.addEventListener("popstate", onPop);
      return () => window.removeEventListener("popstate", onPop);
    } else if (pushedHistory.current && !closingViaPopstate.current) {
      // Closed via X / ESC / provider click — consume the pushed history
      // entry so back button doesn't leave the user on a stale URL state.
      pushedHistory.current = false;
      try { window.history.back(); } catch { /* no-op */ }
    } else {
      closingViaPopstate.current = false;
    }
  }, [email]);

  const handleProvider = (p) => (e) => {
    e.preventDefault();
    if (!email) return;
    const url = p.url(email);
    // Open the tab FIRST (synchronous, keeps user-gesture context so no
    // popup block), then kick off the async copy in the background.
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      toast.error("Il browser ha bloccato l'apertura di una nuova scheda", {
        description: "Consenti i popup per questo sito e riprova.",
      });
    }
    if (p.copyFirst) {
      copyToClipboard(email).then((ok) => {
        if (ok) toast.success("Indirizzo email copiato negli appunti", {
          description: `${email} — incollalo nel campo destinatario.`,
        });
      });
    }
    closeLater();
  };

  const handleDefaultClient = (e) => {
    e.preventDefault();
    if (!email) return;
    // Copy first as a safety net (in case no mail client is configured),
    // then try the mailto: — if nothing opens, the address is on clipboard.
    copyToClipboard(email).then((ok) => {
      if (ok) toast.info("Indirizzo copiato come sicurezza", {
        description: "Se il client di posta non si apre, incollalo dove preferisci.",
      });
    });
    // Trigger mailto in the same user gesture.
    window.location.href = `mailto:${email}`;
    closeLater();
  };

  const handleCopyOnly = async () => {
    if (!email) return;
    const ok = await copyToClipboard(email);
    if (ok) toast.success("Indirizzo email copiato", { description: email });
    else toast.error("Impossibile copiare l'indirizzo", { description: email });
    closeLater();
  };

  return (
    <EmailComposeContext.Provider value={{ open, close }}>
      {children}
      <Dialog open={!!email} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent data-testid="email-compose-dialog" className="max-w-lg p-4 sm:p-6 gap-3 sm:gap-4 max-h-[92vh] overflow-y-auto">
          <DialogHeader className="pr-8">
            <DialogTitle className="font-head font-black text-xl sm:text-2xl tracking-tightest flex items-center gap-2">
              <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-primary shrink-0" /> Scrivi email
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              A: <span className="font-mono break-all text-foreground">{email}</span>
              <br />
              Scegli il servizio di posta che utilizzi.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {PROVIDERS.map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={handleProvider(p)}
                data-testid={p.testid}
                className="group flex items-start gap-3 border border-border p-3 sm:p-4 hover:border-primary hover:bg-primary/5 transition-colors text-left cursor-pointer bg-transparent w-full"
              >
                <ExternalLink className="w-4 h-4 mt-0.5 text-muted-foreground group-hover:text-primary shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block font-head font-bold text-sm sm:text-base tracking-tight">{p.label}</span>
                  {p.note && <span className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1 leading-tight">{p.note}</span>}
                </span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-3 sm:pt-4 border-t border-border">
            <button
              type="button"
              onClick={handleDefaultClient}
              data-testid="email-compose-default-client"
              className="group flex items-start gap-3 border border-border p-3 sm:p-4 hover:border-primary hover:bg-primary/5 transition-colors text-left cursor-pointer bg-transparent"
            >
              <Mail className="w-4 h-4 mt-0.5 text-muted-foreground group-hover:text-primary shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block font-head font-bold text-sm sm:text-base tracking-tight">Client di posta predefinito</span>
                <span className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1 leading-tight">Indirizzo copiato come sicurezza</span>
              </span>
            </button>
            <button
              type="button"
              onClick={handleCopyOnly}
              data-testid="email-compose-copy"
              className="group flex items-center gap-3 border border-border p-3 sm:p-4 hover:border-primary hover:bg-primary/5 transition-colors text-left cursor-pointer bg-transparent"
            >
              <Copy className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
              <span className="font-head font-bold text-sm sm:text-base tracking-tight">Copia indirizzo</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </EmailComposeContext.Provider>
  );
}
